import { ConsultationMode } from "../../../constants/consultationMode";
import { FEE_AUDIENCES } from "../../../constants/feeAudience";
import { resolveAppointmentSystemType, } from "../../../utils/resolveAppointmentSystemType";
export const feeAudience = (fee) => fee.international ? "INTERNATIONAL" : "DOMESTIC";
export function getDoctorConsultationFees(doctor) {
    if (resolveAppointmentSystemType(doctor) === "QMS") {
        return doctor.qmsConsultationCharges ?? [];
    }
    return doctor.consultationFees ?? [];
}
function isChargeableFee(fee) {
    return (typeof fee.currency === "string" &&
        fee.currency.trim() !== "" &&
        typeof fee.amount === "number" &&
        Number.isFinite(fee.amount) &&
        fee.amount >= 0);
}
export function getDoctorConsultationFee(doctor, mode, audience) {
    return getDoctorConsultationFees(doctor).find((fee) => fee.consultationMode === mode &&
        feeAudience(fee) === audience &&
        isChargeableFee(fee));
}
export function getDoctorFeeAudienceOptions(doctor, mode) {
    return FEE_AUDIENCES.flatMap((audience) => {
        const fee = getDoctorConsultationFee(doctor, mode, audience);
        return fee ? [{ audience, currency: fee.currency }] : [];
    });
}
export function preferredFeeAudience(serverInternational) {
    return serverInternational === true ? "INTERNATIONAL" : "DOMESTIC";
}
export function getDoctorConsultationModes(doctor) {
    const permission = doctor.appointmentTypeAndPermission;
    const modes = [];
    if (permission?.allowOffline === true)
        modes.push(ConsultationMode.OFFLINE);
    if (permission?.allowOnline === true && permission.canHostOnline === true) {
        modes.push(ConsultationMode.ONLINE);
    }
    return modes;
}
export function doctorSupportsConsultationMode(doctor, mode) {
    return getDoctorConsultationModes(doctor).includes(mode);
}
export function getAddressesWithDoctors(workspace) {
    const byId = new Map();
    const addFrom = (addresses) => {
        for (const address of addresses ?? []) {
            let entry = byId.get(address.id);
            if (!entry) {
                entry = {
                    id: address.id,
                    city: address.city,
                    fullAddress: address.fullAddress,
                    doctors: [],
                    seen: new Set(),
                };
                byId.set(address.id, entry);
            }
            for (const doctor of address.doctors ?? []) {
                if (entry.seen.has(doctor.id))
                    continue;
                entry.seen.add(doctor.id);
                entry.doctors.push(doctor);
            }
        }
    };
    addFrom(workspace.addresses);
    for (const specialization of workspace.specializations ?? []) {
        addFrom(specialization.addresses);
    }
    return [...byId.values()].map(({ id, city, fullAddress, doctors }) => ({
        id,
        city,
        fullAddress,
        doctors,
    }));
}
const isVisible = (doctorId, visibleIds) => visibleIds === null || visibleIds.includes(doctorId);
export function getVisibleDoctorsForAddress(workspace, addressId, visibleIds) {
    const address = getAddressesWithDoctors(workspace).find((a) => a.id === addressId);
    if (!address)
        return [];
    return address.doctors.filter((d) => isVisible(d.id, visibleIds));
}
export function getVisibleLocations(workspace, visibleIds) {
    return getAddressesWithDoctors(workspace)
        .filter((a) => a.doctors.some((d) => isVisible(d.id, visibleIds)))
        .map(({ id, city, fullAddress }) => ({ id, city, fullAddress }));
}
export function findFirstVisibleDoctorInAddress(workspace, addressId, visibleIds, mode) {
    const doctors = getVisibleDoctorsForAddress(workspace, addressId, visibleIds);
    return (doctors.find((d) => doctorSupportsConsultationMode(d, mode)) ??
        doctors[0] ??
        null);
}
